import React from 'react';
import { Badge, Button, Dropdown, Empty, List, Typography, Tooltip, Tag } from 'antd';
import { BellOutlined, DeleteOutlined, CheckCircleOutlined, InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';
import useNotifications from '../hooks/useNotifications';

const { Text, Title } = Typography;

const NotificationBell = () => {
  const { 
    notifications, 
    unreadCount, 
    markAllAsRead, 
    markAsRead, 
    deleteNotification,
    clearNotifications 
  } = useNotifications();

  const getNotificationIcon = (type) => {
    const iconProps = { className: 'text-lg' };
    switch (type) {
      case 'success':
        return <CheckCircleOutlined className="text-green-500" {...iconProps} />;
      case 'warning':
        return <WarningOutlined className="text-yellow-500" {...iconProps} />;
      default:
        return <InfoCircleOutlined className="text-blue-500" {...iconProps} />;
    }
  };

  const NotificationList = () => (
    <div className="w-96 max-h-[500px] overflow-hidden rounded-xl shadow-xl bg-white border border-neutral-100">
      <div className="p-4 border-b border-neutral-100 bg-white sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Title level={5} className="!mb-0">Notifications</Title>
            {unreadCount > 0 && (
              <Tag color="primary" className="rounded-full">
                {unreadCount} nouvelle{unreadCount > 1 ? 's' : ''}
              </Tag>
            )}
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Tooltip title="Tout marquer comme lu">
                <Button 
                  type="text"
                  size="small"
                  icon={<CheckCircleOutlined />}
                  className="text-primary-600 hover:text-primary-700"
                  onClick={markAllAsRead}
                />
              </Tooltip>
            )}
            {notifications.length > 0 && (
              <Tooltip title="Effacer toutes les notifications">
                <Button 
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={clearNotifications}
                />
              </Tooltip>
            )}
          </div>
        </div>
      </div>
      
      <div className="overflow-y-auto max-h-[400px] scrollbar-thin scrollbar-thumb-neutral-200">
        <AnimatePresence mode="popLayout">
          {notifications.length > 0 ? (
            <List
              dataSource={notifications}
              renderItem={(notif) => (
                <motion.div
                  key={notif.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <div 
                    className={`
                      group p-4 border-b border-neutral-100 cursor-pointer
                      ${!notif.read ? 'bg-primary-50/50' : 'hover:bg-neutral-50'}
                      transition-all duration-200
                    `}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-1">{getNotificationIcon(notif.type)}</div>
                      <div className="flex-1" onClick={() => markAsRead(notif.id)}>
                        <Text strong className="text-sm text-neutral-800 block">
                          {notif.title}
                        </Text>
                        <Text className="text-xs text-neutral-500 block mt-1">
                          {notif.message}
                        </Text>
                        <div className="flex items-center justify-between mt-2">
                          <Text className="text-xs text-neutral-400">
                            {notif.time}
                          </Text>
                          {!notif.read && (
                            <Tag color="primary" className="rounded-full text-xs">
                              Nouveau
                            </Tag>
                          )}
                        </div>
                      </div>
                      <motion.div 
                        initial={{ opacity: 0 }}
                        whileHover={{ opacity: 1 }}
                        className="opacity-0 group-hover:opacity-100"
                      >
                        <Tooltip title="Supprimer">
                          <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNotification(notif.id);
                            }}
                          />
                        </Tooltip>
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              )}
            />
          ) : (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="py-12"
            >
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Text className="text-neutral-500">
                    Aucune notification
                  </Text>
                }
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      <div className="p-3 border-t border-neutral-100 bg-neutral-50/50 sticky bottom-0">
        <Button 
          type="link" 
          block
          className="text-neutral-600 hover:text-primary-600"
        >
          Voir l'historique complet
        </Button>
      </div>
    </div>
  );

  return (
    <Dropdown
      dropdownRender={() => <NotificationList />}
      trigger={['click']}
      placement="bottomRight"
      arrow={{ pointAtCenter: true }}
    >
      <motion.div
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <Badge 
          count={unreadCount} 
          className="cursor-pointer"
          style={{ backgroundColor: '#4F46E5' }}
        >
          <Button
            type="text"
            icon={<BellOutlined />}
            className="w-10 h-10 rounded-xl hover:bg-primary-50 hover:text-primary-600 transition-colors duration-200"
          />
        </Badge>
      </motion.div>
    </Dropdown>
  );
};

export default NotificationBell; 